import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { NotchFilterVisualization } from '../components/NotchFilterVisualization';
import { InteractiveNotchDiagramResponsive } from '../components/InteractiveNotchDiagramResponsive';

const HowItWorksExpanded: React.FC = () => {
  const [showDetailedExplanation, setShowDetailedExplanation] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col how-it-works-page">
      {/* Simple Back Button */}
      <div className="container mx-auto px-4 py-6">
        <Link 
          to="/"
          className="inline-flex items-center gap-2 text-purple-600 hover:text-purple-700 transition-colors font-medium"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Home
        </Link>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 pb-8 flex-grow">
        <div className="max-w-4xl mx-auto">
          {/* Interactive Demo Section - Hero */}
          <section className="mb-12">
            <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
              <div className="text-center mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-purple-700 mb-3">
                  🎵 See Your Frequency Get Removed
                </h2>
                <p className="text-gray-600 text-sm sm:text-base">
                  Drag the slider to see how notched sound therapy works
                </p>
              </div>
              
              {/* Interactive Visualization */}
              <InteractiveNotchDiagramResponsive />
              
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500">
                  The pink gap shows where your tinnitus frequency gets filtered out of white noise
                </p>
              </div>
            </div>
          </section>

          {/* Research Credibility Section */}
          <section className="mb-12">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6 border border-purple-100">
              <h2 className="text-xl sm:text-2xl font-bold text-purple-700 mb-4 text-center">
                Research-Backed Approach
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                <div className="flex flex-col items-center">
                  <div className="text-2xl mb-2">✅</div>
                  <h3 className="font-semibold text-purple-700 mb-1">Clinically Validated</h3>
                  <p className="text-sm text-gray-600">Based on peer-reviewed studies</p>
                </div>
                <div className="flex flex-col items-center">
                  <div className="text-2xl mb-2">📊</div>
                  <h3 className="font-semibold text-purple-700 mb-1">20% Reduction</h3>
                  <p className="text-sm text-gray-600">Average tinnitus loudness decrease</p>
                </div>
                <div className="flex flex-col items-center">
                  <div className="text-2xl mb-2">🔬</div>
                  <h3 className="font-semibold text-purple-700 mb-1">0.5 Octave Standard</h3>
                  <p className="text-sm text-gray-600">Research-optimized bandwidth</p>
                </div>
              </div>
            </div>
          </section>
          
          {/* Simple Process Section */}
          <section className="mb-12">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 mb-8 text-center">
              How It Works: 3 Simple Steps
            </h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  🎯
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">1. Find</h3>
                <p className="text-gray-600 text-sm">
                  Quick test finds your exact tinnitus frequency in minutes
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  🎵
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">2. Listen</h3>
                <p className="text-gray-600 text-sm">
                  Personalized sound therapy removes your specific frequency
                </p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-pink-500 to-pink-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  🧠
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">3. Relief</h3>
                <p className="text-gray-600 text-sm">
                  Your brain gradually adapts and reduces focus on the tinnitus
                </p>
              </div>
            </div>
          </section>

          {/* Live Demo Section */}
          <section className="mb-12">
            <div className="bg-white rounded-lg shadow-lg p-6">
              <div className="text-center mb-6">
                <h2 className="text-xl sm:text-2xl font-bold text-purple-700 mb-3">
                  🔊 Try the Interactive Demo
                </h2>
                <p className="text-gray-600">
                  Adjust frequency and hear the notch effect (use headphones)
                </p>
              </div>
              
              <NotchFilterVisualization />
            </div>
          </section>

          {/* Progressive Disclosure for Technical Details */}
          <section className="mb-12">
            <div className="text-center">
              <button
                onClick={() => setShowDetailedExplanation(!showDetailedExplanation)}
                className="inline-flex items-center gap-2 px-4 py-2 text-purple-600 hover:text-purple-700 font-medium transition-colors"
              >
                <span>{showDetailedExplanation ? 'Hide' : 'Show'} Technical Details</span>
                <span className="text-sm">{showDetailedExplanation ? '▲' : '▼'}</span>
              </button>
            </div>
            
            {showDetailedExplanation && (
              <div className="mt-6 bg-gray-50 rounded-lg p-6">
                <div className="space-y-6">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">How the Algorithm Works</h3>
                    <div className="space-y-4 text-gray-700 text-sm">
                      <p>
                        Smart algorithm narrows down your frequency fast using a binary search approach.
                        Instead of testing hundreds of frequencies randomly, we intelligently eliminate 
                        half the possibilities with each comparison.
                      </p>
                      <p>
                        <strong>Result:</strong> Find your exact frequency from thousands of possibilities 
                        in just 8-12 steps, typically taking 3-5 minutes.
                      </p>
                      <div className="bg-white rounded p-3 text-xs font-mono text-gray-600">
                        Example: Finding frequency between 250-12,000 Hz:
                        <br />Step 1: Test middle point (6,125 Hz)
                        <br />Step 2: If your tinnitus is lower, test 3,187 Hz  
                        <br />Step 3: Continue halving until exact match
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Web Audio Technology</h3>
                    <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
                      <div>
                        <h4 className="font-semibold mb-2">Audio Quality</h4>
                        <ul className="space-y-1">
                          <li>• Pure sine wave generation</li>
                          <li>• 44,100 Hz sample rate</li>
                          <li>• Sub-millisecond precision</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">Performance</h4>
                        <ul className="space-y-1">
                          <li>• Average: 8-12 questions</li>
                          <li>• Accuracy: ±5 Hz</li>
                          <li>• Success rate: &gt;95%</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Tips for Best Results</h3>
                    <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
                      <div>
                        <h4 className="font-semibold mb-2">Setup</h4>
                        <ul className="space-y-1">
                          <li>• Use quality headphones</li>
                          <li>• Find quiet environment</li>
                          <li>• Comfortable volume level</li>
                        </ul>
                      </div>
                      <div>
                        <h4 className="font-semibold mb-2">During Test</h4>
                        <ul className="space-y-1">
                          <li>• Take your time</li>
                          <li>• Focus on pitch, not volume</li>
                          <li>• Trust your instincts</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* CTA Section */}
          <section className="text-center">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg p-6 sm:p-8 border border-purple-100">
              <h2 className="text-xl sm:text-2xl font-bold text-purple-700 mb-3">
                Ready to Try It?
              </h2>
              <p className="text-gray-700 mb-6 text-sm sm:text-base">
                Free test • No registration • 5 minutes
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center max-w-md mx-auto">
                <Link
                  to="/"
                  className="flex-1 px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-lg transition-all duration-200 shadow-lg transform hover:scale-105 text-center"
                >
                  🎯 Start Free Test
                </Link>
                <Link
                  to="/therapy"
                  className="flex-1 px-6 py-3 bg-white border-2 border-purple-600 text-purple-600 hover:bg-purple-50 font-bold rounded-lg transition-all duration-200 text-center"
                >
                  🎵 Go to Therapy
                </Link>
              </div>
              
              <p className="text-xs text-gray-500 mt-4">
                Works on any device with speakers or headphones
              </p>
            </div>
          </section>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HowItWorksExpanded;